import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api, formatPrice, API } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Copy, Check, Download, Key, Package, User, FileText } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";

export default function Account() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("vault");

  useEffect(() => {
    if (ready && !user) navigate("/prihlaseni");
  }, [user, ready, navigate]);

  useEffect(() => {
    if (user) api.get("/my/orders").then((r) => setOrders(r.data));
  }, [user]);

  if (!user) return null;

  return (
    <div className="pt-8 pb-20" data-testid="account-page">
      <SEO title="Můj účet" noindex />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-display font-bold text-xl">
            {(user.first_name || user.email)[0].toUpperCase()}
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold">{user.first_name} {user.last_name}</h1>
            <div className="text-sm text-zinc-500">{user.email}</div>
          </div>
        </div>

        <div className="flex gap-1 border-b border-zinc-200 mb-8">
          <Tab id="vault" current={tab} setTab={setTab} icon={Key} label="Licenční trezor"/>
          <Tab id="orders" current={tab} setTab={setTab} icon={Package} label="Objednávky"/>
          <Tab id="profile" current={tab} setTab={setTab} icon={User} label="Profil"/>
        </div>

        {tab === "vault" && (
          <div className="space-y-4" data-testid="vault-tab">
            {orders.filter((o) => o.payment_status === "paid").length === 0 ? (
              <div className="text-center py-16 text-zinc-500">Zatím nemáte žádné zakoupené licence.</div>
            ) : orders.filter((o) => o.payment_status === "paid").map((o) => (
              <OrderVaultCard key={o.id} order={o}/>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-3" data-testid="orders-tab">
            {orders.length === 0 ? <div className="text-zinc-500 text-center py-12">Žádné objednávky.</div> : orders.map((o) => (
              <div key={o.id} className="bg-white rounded-xl border border-zinc-200 p-5 flex justify-between items-center">
                <div>
                  <div className="font-semibold">{o.order_number}</div>
                  <div className="text-xs text-zinc-500">{new Date(o.created_at).toLocaleDateString("cs-CZ")} • {o.items.length} položek</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold">{formatPrice(o.total)}</div>
                  <StatusBadge status={o.payment_status}/>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "profile" && (
          <div className="bg-white rounded-2xl border border-zinc-200 p-6 max-w-2xl">
            <div className="grid grid-cols-2 gap-4">
              <Info label="Jméno" value={`${user.first_name || ""} ${user.last_name || ""}`}/>
              <Info label="E-mail" value={user.email}/>
              <Info label="Telefon" value={user.phone || "—"}/>
              <Info label="Společnost" value={user.company || "—"}/>
              <Info label="IČO" value={user.ic || "—"}/>
              <Info label="DIČ" value={user.dic || "—"}/>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Tab({ id, current, setTab, icon: Icon, label }) {
  return (
    <button onClick={() => setTab(id)} className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${current === id ? "border-blue-600 text-blue-600" : "border-transparent text-zinc-500 hover:text-zinc-900"}`} data-testid={`acc-tab-${id}`}>
      <Icon className="w-4 h-4"/>{label}
    </button>
  );
}

function KeyCard({ k }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(k.key);
    setCopied(true);
    toast.success("Klíč zkopírován!");
    setTimeout(() => setCopied(false), 2000);
  };
  const hasLinks = k.download_url || k.activation_url;
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-3 space-y-2">
      <div className="flex items-center gap-3">
        <Key className="w-4 h-4 text-blue-600 shrink-0"/>
        <code className="font-mono text-sm flex-1 break-all">{k.key}</code>
        <button onClick={copy} className="p-2 hover:bg-white rounded-lg" data-testid="copy-key">
          {copied ? <Check className="w-4 h-4 text-emerald-600"/> : <Copy className="w-4 h-4 text-zinc-500"/>}
        </button>
      </div>
      {hasLinks && (
        <div className="flex flex-wrap gap-2 pl-7" data-testid={`key-links-${k.id}`}>
          {k.download_url && (
            <a href={k.download_url} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold transition-colors"
               data-testid={`key-download-${k.id}`}>
              <Download className="w-3.5 h-3.5"/>Stáhnout instalační soubor
            </a>
          )}
          {k.activation_url && (
            <a href={k.activation_url} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold transition-colors"
               data-testid={`key-activation-${k.id}`}>
              <FileText className="w-3.5 h-3.5"/>Aktivační stránka / návod
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: { color: "bg-emerald-100 text-emerald-700", text: "Zaplaceno" },
    pending: { color: "bg-amber-100 text-amber-700", text: "Čeká na platbu" },
    failed: { color: "bg-red-100 text-red-700", text: "Selhalo" },
    refunded: { color: "bg-zinc-100 text-zinc-700", text: "Vráceno" },
  };
  const s = map[status] || map.pending;
  return <span className={`label-pill ${s.color}`}>{s.text}</span>;
}

function OrderVaultCard({ order: o }) {
  const [vaultFiles, setVaultFiles] = useState([]);
  useEffect(() => {
    let active = true;
    api.get(`/orders/${o.id}/files`)
      .then((r) => { if (active) setVaultFiles(r.data || []); })
      .catch(() => { if (active) setVaultFiles([]); });
    return () => { active = false; };
  }, [o.id]);

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 p-6">
      <div className="flex justify-between items-center mb-4 pb-4 border-b border-zinc-100">
        <div>
          <div className="font-display font-bold">{o.order_number}</div>
          <div className="text-xs text-zinc-500">{new Date(o.created_at).toLocaleDateString("cs-CZ")}</div>
        </div>
        {o.invoice_number && (
          <a href={`${API}/orders/${o.id}/invoice.pdf?token=${encodeURIComponent(localStorage.getItem("ss_token") || "")}`} target="_blank" rel="noopener" className="inline-flex items-center gap-1.5 text-sm text-blue-600 font-semibold hover:underline" data-testid={`invoice-${o.order_number}`}><Download className="w-4 h-4"/>Faktura {o.invoice_number}</a>
        )}
      </div>
      <div className="space-y-6">
        {o.items.map((it, i) => {
          const itemFiles = vaultFiles.filter((f) => (f.product_ids || []).includes(it.product_id));
          return (
            <div key={i} className="space-y-3">
              <div className="font-semibold text-sm">{it.product_name} × {it.quantity}</div>
              {it.delivered_keys?.length > 0 ? (
                <div className="space-y-2">
                  {it.delivered_keys.map((k) => <KeyCard key={k.id} k={k}/>)}
                </div>
              ) : (
                <div className="text-xs text-zinc-500">Klíče se připravují…</div>
              )}
              {itemFiles.length > 0 && <ItemDownloads files={itemFiles} itemIndex={i}/>}
              {it.product_guides && it.product_guides.length > 0 && (
                <div className="pl-3 border-l-2 border-blue-100" data-testid={`guides-${i}`}>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">Návody ke stažení</div>
                  <div className="flex flex-wrap gap-2">
                    {it.product_guides.map((g, gi) => (
                      <a key={gi} href={g.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold transition-colors" data-testid={`guide-${i}-${gi}`}>
                        <FileText className="w-3.5 h-3.5"/>
                        <span>{g.name}</span>
                        {g.size ? <span className="text-blue-400 font-normal">({(g.size/1024/1024).toFixed(1)} MB)</span> : null}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ItemDownloads({ files, itemIndex }) {
  const fmtSize = (n) => {
    if (!n) return "";
    const u = ["B","KB","MB","GB"]; let i = 0; let v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 100 ? 0 : 1)} ${u[i]}`;
  };
  return (
    <div className="pl-3 border-l-2 border-emerald-100" data-testid={`item-downloads-${itemIndex}`}>
      <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-2">Instalační soubory ke stažení</div>
      <div className="flex flex-wrap gap-2">
        {files.map((f) => (
          <a
            key={f.id}
            href={f.slug ? `/download/${f.slug}` : "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold transition-colors"
            data-testid={`download-file-${f.id}`}
          >
            <Download className="w-3.5 h-3.5"/>
            <span>{f.title || f.filename}</span>
            {f.version ? <span className="text-emerald-500 font-normal">v{f.version}</span> : null}
            {f.size ? <span className="text-emerald-500 font-normal">({fmtSize(f.size)})</span> : null}
          </a>
        ))}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return <div><div className="text-xs text-zinc-500 mb-1">{label}</div><div className="font-medium">{value}</div></div>;
}
